import { coerceCalendarDate } from "../../../utils/calendarDateKey";
import { formatMedicationDose } from "../../../shared/medicationDose";
const normalizeText = (value = "") => String(value || "").trim();
const formatMedicationLoggedTime = (value) => {
  const date = coerceCalendarDate(value);
  if (!date) {
    return normalizeText(value);
  }

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const medicationScheduleTypeLabels = {
  scheduled: "Scheduled",
  specific_days: "Specific days",
  as_needed: "As needed",
  occasionally: "Occasional",
  short_course: "Short course",
  every_x_hours: "Every X hours",
};

export const buildMedicationLogRows = (entry = {}) => {
  // Ambiguous medication administrations have their own explicit review UI.
  // Do not infer "Given" from the generic entry status or duplicate confirmed
  // medication lines rendered by that review surface.
  if (entry.metadata?.medicationAdministrationReview) {
    return [];
  }

  const rows = [];
  const actualDose = normalizeText(
    entry.actualDose ||
      entry.givenDose ||
      entry.dose ||
      entry.medicationScheduleSnapshot?.dose ||
      entry.medicationScheduleDose ||
      "",
  );
  const plannedDose = normalizeText(
    entry.plannedDose ||
      entry.medicationScheduleSnapshot?.dose ||
      entry.medicationScheduleDose ||
      "",
  );
  const unit = normalizeText(
    entry.medicationScheduleSnapshot?.unit ||
      entry.medicationScheduleUnit ||
      entry.unit ||
      "",
  );
  const doseValue = formatMedicationDose({ dose: actualDose, unit });
  const plannedValue = formatMedicationDose({ dose: plannedDose, unit });
  const state = normalizeText(
    entry.medicationLogState || entry.status,
  ).toLowerCase();
  const scheduleType = normalizeText(
    entry.medicationScheduleType || entry.medicationFrequency || "",
  ).toLowerCase();
  const scheduleLabel = medicationScheduleTypeLabels[scheduleType] || "";
  const courseDayLabel = normalizeText(entry.medicationCourseDayLabel);
  const intervalHours = normalizeText(entry.medicationIntervalHours);
  const intervalStartTime = normalizeText(entry.medicationIntervalStartTime);
  const asNeededReason = normalizeText(entry.medicationAsNeededReason);
  const maxFrequency = normalizeText(entry.medicationMaxFrequency);
  const effectLabel = normalizeText(entry.medicationEffectLabel);
  const updatedAt = coerceCalendarDate(entry.updatedAt);
  const createdAt = coerceCalendarDate(entry.createdAt);
  const wasEdited =
    Boolean(
      updatedAt && createdAt && updatedAt.getTime() !== createdAt.getTime(),
    ) ||
    (actualDose && plannedDose && actualDose !== plannedDose);

  const isMissed = state === "missed" || state === "skipped";
  const isReminderReceipt =
    entry.provenance?.kind === "medication_reminder_occurrence";
  if (!isReminderReceipt) {
    rows.push({
      label: "Status",
      value: isMissed ? "Missed" : "Given",
    });
  }

  if (doseValue) {
    rows.push({
      label: isReminderReceipt ? "Dose" : isMissed ? "Planned" : "Given",
      value: doseValue,
    });
  }

  if (scheduleLabel) {
    const scheduleDetails = [];
    if (scheduleType === "short_course" && courseDayLabel) {
      scheduleDetails.push(courseDayLabel);
    }
    if (scheduleType === "every_x_hours" && intervalHours) {
      scheduleDetails.push(`Every ${intervalHours} hours`);
    }
    if (scheduleType === "every_x_hours" && intervalStartTime) {
      scheduleDetails.push(
        `from ${formatMedicationLoggedTime(`1970-01-01T${intervalStartTime}:00`) || intervalStartTime}`,
      );
    }
    rows.push({
      label: "Schedule",
      value: [scheduleLabel, scheduleDetails.join(" · ")]
        .filter(Boolean)
        .join(" · "),
    });
  }

  if (asNeededReason || maxFrequency) {
    rows.push({
      label: "Use when",
      value: [asNeededReason, maxFrequency].filter(Boolean).join(" · "),
    });
  }

  if (effectLabel) {
    rows.push({
      label: "Did it help?",
      value: effectLabel,
    });
  }

  if (wasEdited && plannedValue && plannedValue !== doseValue) {
    rows.push({ label: "Planned", value: plannedValue });
  }

  if (wasEdited && entry.updatedAt) {
    rows.push({
      label: "Edited",
      value: formatMedicationLoggedTime(entry.updatedAt),
    });
  }

  return rows;
};
